import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import apiService from '../../services/apiServices';
import Toast from '../actions/Toasts';
import useConfirm from '../../hooks/useConfirm';

const alert = { open: false, message: '', variant: 'info' };

const toDatetimeLocal = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const offset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offset).toISOString().slice(0, 16);
};

const minutesBetween = (startVal, endVal) => {
  const start = new Date(startVal);
  const end = new Date(endVal);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const diff = Math.round((end.getTime() - start.getTime()) / 60000);
  return diff >= 0 ? diff : null;
};

const formatMinutes = (minutes) => {
  if (minutes === null || minutes === undefined || minutes === '') return '-';
  const total = Number(minutes);
  if (!Number.isFinite(total)) return '-';
  const rounded = Math.max(0, Math.round(total));
  const hrs = Math.floor(rounded / 60);
  const mins = rounded % 60;
  if (!hrs && !mins) return '0m';
  return `${hrs ? `${hrs}h ` : ''}${mins}m`.trim();
};

export default function ActivitiesList() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState(alert);
  const [formData, setFormData] = useState({
    project_id: '',
    user_id: '',
    activity_date: '',
    start_at: '',
    end_at: '',
    hours: '',
    description: '',
  });

  useEffect(() => {
    loadAll();
  }, []);

  const clientMap = useMemo(() => {
    const map = new Map();
    clients.forEach((c) => map.set(String(c.id), c));
    return map;
  }, [clients]);

  const projectMap = useMemo(() => {
    const map = new Map();
    projects.forEach((p) => map.set(String(p.id), p));
    return map;
  }, [projects]);

  const userMap = useMemo(() => {
    const map = new Map();
    users.forEach((u) => map.set(String(u.id), u));
    return map;
  }, [users]);

  const loadAll = async () => {
    await Promise.all([loadActivities(), loadProjects(), loadClients(), loadUsers()]);
  };

  const loadActivities = async () => {
    try {
      const response = await apiService().get('projectTimeEntries');
      setRows(response.data || []);
    } catch (_) {
      setToast({ open: true, message: 'Gabim në ngarkimin e aktiviteteve', variant: 'error' });
    }
  };

  const loadProjects = async () => {
    try {
      const response = await apiService().get('projects');
      setProjects(response.data || []);
    } catch (_) {
      setProjects([]);
    }
  };

  const loadClients = async () => {
    try {
      const response = await apiService().get('clients');
      setClients(response.data || []);
    } catch (_) {
      setClients([]);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await apiService().get('cms_users');
      setUsers(response.data || []);
    } catch (_) {
      setUsers([]);
    }
  };

  const handleOpenDialog = (row = null) => {
    if (row) {
      setEditing(row);
      setFormData({
        project_id: row.project_id || '',
        user_id: row.user_id || '',
        activity_date: row.activity_date ? String(row.activity_date).slice(0, 10) : '',
        start_at: toDatetimeLocal(row.start_at),
        end_at: toDatetimeLocal(row.end_at),
        hours: row.minutes != null ? String(Math.round((Number(row.minutes) / 60) * 100) / 100) : '',
        description: row.description || '',
      });
    } else {
      setEditing(null);
      setFormData({
        project_id: '',
        user_id: '',
        activity_date: '',
        start_at: '',
        end_at: '',
        hours: '',
        description: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditing(null);
  };

  const handleSave = async () => {
    if (!formData.project_id) {
      setToast({ open: true, message: 'Projekti është i detyrueshëm', variant: 'error' });
      return;
    }
    try {
      let minutes = null;
      if (formData.hours !== '') {
        const hours = Number(formData.hours);
        minutes = Number.isFinite(hours) ? Math.round(hours * 60) : null;
      }
      if (minutes == null && formData.start_at && formData.end_at) {
        minutes = minutesBetween(formData.start_at, formData.end_at);
      }
      const activityDate =
        formData.activity_date ||
        (formData.start_at ? formData.start_at.split('T')[0] : new Date().toISOString().slice(0, 10));
      const payload = {
        project_id: formData.project_id,
        user_id: formData.user_id || null,
        activity_date: activityDate,
        start_at: formData.start_at || null,
        end_at: formData.end_at || null,
        minutes,
        description: formData.description || null,
      };

      if (editing) {
        // put() derives the id from the payload (Id/id), not a 3rd arg — include it
        // so the update hits /projectTimeEntries/<id> instead of a no-id PUT.
        await apiService().put('projectTimeEntries', { ...payload, id: editing.id });
        setToast({ open: true, message: 'Aktiviteti u përditësua me sukses', variant: 'success' });
      } else {
        await apiService().post('projectTimeEntries', payload);
        setToast({ open: true, message: 'Aktiviteti u krijua me sukses', variant: 'success' });
      }
      handleCloseDialog();
      loadActivities();
    } catch (_) {
      setToast({ open: true, message: 'Gabim në ruajtjen e aktivitetit', variant: 'error' });
    }
  };

  const [confirm, confirmUI] = useConfirm();


  const handleDelete = async (id) => {
    if (!(await confirm({ title: 'Fshi aktivitetin', message: 'A jeni të sigurt që doni të fshini këtë aktivitet?', confirmLabel: 'Fshi' }))) return;
    try {
      await apiService().delete('projectTimeEntries', id);
      setToast({ open: true, message: 'Aktiviteti u fshi me sukses', variant: 'success' });
      loadActivities();
    } catch (_) {
      setToast({ open: true, message: 'Gabim në fshirjen e aktivitetit', variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {confirmUI}
      <Toast {...toast} />
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h5">Aktivitetet</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
              Shto Aktivitet
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Projekti</TableCell>
                  <TableCell>Klienti</TableCell>
                  <TableCell>Punëtori</TableCell>
                  <TableCell>Orë</TableCell>
                  <TableCell>Përshkrimi</TableCell>
                  <TableCell align="right">Veprime</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const project = projectMap.get(String(row.project_id));
                  const client = project?.client_id ? clientMap.get(String(project.client_id)) : null;
                  const user = row.user_id ? userMap.get(String(row.user_id)) : null;
                  return (
                    <TableRow key={row.id}>
                      <TableCell>{row.activity_date ? String(row.activity_date).slice(0, 10) : '-'}</TableCell>
                      <TableCell>{project?.name || '-'}</TableCell>
                      <TableCell>{client?.name || '-'}</TableCell>
                      <TableCell>{user ? `${user.name || ''} ${user.surname || ''}`.trim() : '-'}</TableCell>
                      <TableCell>{formatMinutes(row.minutes)}</TableCell>
                      <TableCell>{row.description || '-'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="primary" onClick={() => handleOpenDialog(row)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary">Nuk ka aktivitete për të shfaqur</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edito Aktivitetin' : 'Shto Aktivitet'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              select
              label="Projekti"
              fullWidth
              required
              value={formData.project_id}
              onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
            >
              <MenuItem value="">
                <em>Zgjidh projektin</em>
              </MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Punëtori"
              fullWidth
              value={formData.user_id}
              onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
            >
              <MenuItem value="">
                <em>Pa punëtor</em>
              </MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {(user.name || '').trim()} {(user.surname || '').trim()}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Data"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.activity_date}
              onChange={(e) => setFormData({ ...formData, activity_date: e.target.value })}
            />
            <TextField
              label="Fillimi"
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.start_at}
              onChange={(e) => setFormData({ ...formData, start_at: e.target.value })}
            />
            <TextField
              label="Mbarimi"
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.end_at}
              onChange={(e) => setFormData({ ...formData, end_at: e.target.value })}
            />
            <TextField
              label="Orë"
              type="number"
              fullWidth
              inputProps={{ step: 0.25, min: 0 }}
              value={formData.hours}
              onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
            />
            <TextField
              label="Përshkrimi"
              fullWidth
              multiline
              minRows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Anulo</Button>
          <Button onClick={handleSave} variant="contained">
            Ruaj
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
